import lodash from 'lodash';
import moment from 'moment';
import { getTimezoneOffset } from 'common/selectors/index';
import { applyTimeZoneToDate } from 'common/util/dateTime';
import { composeReducers } from 'common/util/composeReducers';
import { getStudyDetails } from '../../study/reducers/createStudy';
import {
    ADD_STUDY_USER,
    LOAD_STUDY_DESCRIPTORS,
    LOAD_BINDER_DESCRIPTORS,
    LOAD_STUDY_STAGES,
    LOAD_INSTITUTION_STUDY_TEAM_MEMBERS,
    MATCH_STUDY_TEAM_TO_NAME_OF_RESEARCHER,
    UNLOAD_STUDY_SETTINGS_TEAM,
    LOAD_STUDY_ROLES_BY_INSTITUTION,
    PROCESSING_STUDY_SETTINGS,
    LOAD_EXTERNAL_USER_FOR_STUDY,
    LOAD_EXTERNAL_USERS_FOR_STUDY,
    LOAD_EXTERNAL_USERS_FOR_STUDY_WITH_PAGINATION,
    WAITING_FOR_LOAD_EXTERNAL_USER_FOR_STUDY,
    WAITING_FOR_LOAD_EXTERNAL_USERS_FOR_STUDY,
    CLEAR_EXTERNAL_USER_FOR_STUDY,
    SET_STUDY_NOTIFIED_USERS_LOADED_STATUS,
    LOAD_STUDY_NOTIFIED_USERS_SUCCESS,
    SAVE_STUDY_NOTIFIED_USERS_SUCCESS,
    CLEAR_STUDY_NOTIFIED_USERS,
    LOAD_STUDY_TEAM,
    LOAD_ALL_STUDY_TEAM,
    ADDED_STUDY_DELEGATIONS
} from '../actions/studySettings';
import { newDoaStudyFlowAccess } from '../../../common/features/predicates';

const STAFF_METADATA_TYPE = 'staff';

export function processingStudySettings(state, action) {
    if (!action || action.type !== PROCESSING_STUDY_SETTINGS) {
        return state;
    }

    return {
        ...state,
        studySettings: {
            ...state.studySettings,
            processingStudySettings: action.payload
        }
    };
}

export function getStudyRoles(state, action) {
    if (!action || action.type !== LOAD_STUDY_ROLES_BY_INSTITUTION) {
        return state;
    }

    const newState = lodash.cloneDeep(state);
    lodash.set(newState, 'studySettings.studyRoles', action.payload);
    return newState;
}

export function getStudyStages(state, action) {
    if (!action || action.type !== LOAD_STUDY_STAGES) {
        return state;
    }

    const newState = lodash.cloneDeep(state);

    lodash.set(newState, 'studySettings.studyStages', action.payload);

    return newState;
}

export function updateStudyTeam(state, action) {
    if (!action || action.type !== ADD_STUDY_USER) {
        return state;
    }

    const { delegation, ...newUser } = action.payload;

    const newState = lodash.cloneDeep(state);

    const studyTeam = lodash.get(newState, 'studyTeam', []);
    const settingsStudyTeam = lodash.get(newState, 'studySettings.studyTeam', []);

    studyTeam.push(newUser);
    settingsStudyTeam.push(newUser);

    const settingsStudyTeamWithFixedDisplay = settingsStudyTeam.map(user => {
        return {
            ...user,
            displayName: user.user.displayName,
        };
    });


    lodash.set(newState, 'studyTeam', studyTeam);
    lodash.set(newState, 'studySettings.studyTeam', settingsStudyTeamWithFixedDisplay);

    return newState;
}

export function getStudyTeam(state, action) {
    if (!action || ![LOAD_STUDY_TEAM, LOAD_ALL_STUDY_TEAM].includes(action.type)) {
        return state;
    }

    const studyTeam = action.payload.studyTeamMember ? action.payload.studyTeamMember : action.payload;
    const studyTeamCount = action.payload.totalCount || 0;

    const studyTeamUsers = studyTeam.map(user => {
        return {
            ...user,
            displayName: user.user.displayName,
        };
    });

    const newState = lodash.cloneDeep(state);
    const isNewDoaEnabled = newDoaStudyFlowAccess(state);
    if (!isNewDoaEnabled)
        lodash.set(newState, 'studySettings.studyTeam', studyTeamUsers);
    else {
        if (action.type === LOAD_ALL_STUDY_TEAM) {
            lodash.set(newState, 'studySettings.allStudyTeam', studyTeamUsers);

        }
        else {
            lodash.set(newState, 'studySettings.studyTeam', studyTeamUsers);
            lodash.set(newState, 'studySettings.studyTeamCount', studyTeamCount);
        }
    }
    return newState;
}

function getStudyDescriptors(state, action) {
    if (!action || action.type !== LOAD_STUDY_DESCRIPTORS) {
        return state;
    }

    const allStudyDescriptors = action.payload;

    const staffDescriptor = getStaffDescriptor(allStudyDescriptors);
    const studyDescriptors = lodash.without(allStudyDescriptors, staffDescriptor);

    return {
        ...state,
        studySettings: {
            ...state.studySettings,
            staffDescriptor: {
                ...state.studySettings.staffDescriptor,
                ...staffDescriptor
            },
            studyDescriptors: {
                ...state.studySettings.studyDescriptors,
                ...studyDescriptors,
            }
        }
    };
}

function getBinderDescriptors(state, action) {
    if (!action || action.type !== LOAD_BINDER_DESCRIPTORS) {
        return state;
    }

    const {
        institutionId,
        binderTemplate,
        descriptors,
    } = action.payload;
    const binderDescriptors = getStudyDetails(binderTemplate, descriptors, institutionId);

    return {
        ...state,
        studySettings: {
            ...state.studySettings,
            binderDescriptors: [
                ...binderDescriptors
            ]
        }
    };
}

function getStaffDescriptor(studyMetadata) {
    return lodash.find(studyMetadata, metadata => metadata.type === STAFF_METADATA_TYPE);
}

export function getTeamMembersByInstitution(state, action) {
    if (!action || action.type !== LOAD_INSTITUTION_STUDY_TEAM_MEMBERS) {
        return state;
    }
    const allTeamMembers = action.payload.map(user => {
        user.userId = user.id;
        delete user.id;
        return user;
    });
    const newState = lodash.cloneDeep(state);
    lodash.set(newState, 'studySettings.allTeamMembers', allTeamMembers);
    return newState;
}

export function createPotentialResearchersToStudyTeam(state, action) {
    if (!action || action.type !== MATCH_STUDY_TEAM_TO_NAME_OF_RESEARCHER) {
        return state;
    }

    const newState = lodash.cloneDeep(state);
    const newTeamMembers = lodash.flatMap(action.payload);
    lodash.set(newState, 'studySettings.studyTeam', newTeamMembers);
    return newState;
}

export function unloadStudySettingsTeam(state, action) {
    if (!action || action.type !== UNLOAD_STUDY_SETTINGS_TEAM) {
        return state;
    }

    const newState = lodash.cloneDeep(state);
    lodash.unset(newState, 'studySettings.staffDescriptor');
    lodash.unset(newState, 'studySettings.allTeamMembers');
    lodash.unset(newState, 'studySettings.studyStages');
    lodash.unset(newState, 'studySettings.studyRoles');
    return newState;
}

export function loadExternalUserForStudy(state, action) {
    if (!action || action.type !== LOAD_EXTERNAL_USER_FOR_STUDY) {
        return state;
    }

    return {
        ...state,
        'existingExternalUser': {
            ...action.payload,
        }
    };
}

export function waitingForLoadExternalUserForStudy(state, action) {
    if (!action || action.type !== WAITING_FOR_LOAD_EXTERNAL_USER_FOR_STUDY) {
        return state;
    }

    return {
        ...state,
        existingExternalUser: {
            ...state.existingExternalUser,
            loading: action.payload,
        }
    };
}

export function loadExternalUsersForStudy(state, action) {
    if (!action || action.type !== LOAD_EXTERNAL_USERS_FOR_STUDY) {
        return state;
    }

    const externalUsers = action.payload;
    const binderUsers = state.binder.users;
    const timezoneOffset = getTimezoneOffset(state);

    externalUsers.forEach(user => {
        const existingUser = binderUsers.find(u => u.id === user.id);
        if (!existingUser) {
            binderUsers.push({
                id: user.id,
                sub: user.sub,
                displayName: user.displayName,
                institutionId: user.institutionId,
                firstName: user.firstName,
                lastName: user.lastName,
            });
        }
    });

    const timeZoneCorrectedExternalUsers = mapExternalUsersToInstitutionTimezone(externalUsers, timezoneOffset);
    const sortedExternalUsers = sortExternalUsersByStartDate(timeZoneCorrectedExternalUsers);

    return {
        ...state,
        externalUsersForStudy: sortedExternalUsers,
        binder: {
            ...state.binder,
            users: binderUsers,
        }
    };
}
export function loadExternalUsersForStudyWithPagination(state, action) {
    if (!action || action.type !== LOAD_EXTERNAL_USERS_FOR_STUDY_WITH_PAGINATION) {
        return state;
    }
    const externalUsers = action.payload.studyExternalUsersWithPagination;
    const binderUsers = state.binder.users;
    const timezoneOffset = getTimezoneOffset(state);

    externalUsers.externalUsers.forEach(user => {
        const existingUser = binderUsers.find(u => u.id === user.id);
        if (!existingUser) {
            binderUsers.push({
                id: user.id,
                sub: user.sub,
                displayName: user.displayName,
                institutionId: user.institutionId,
                firstName: user.firstName,
                lastName: user.lastName,
            });
        }
    });

    const timeZoneCorrectedExternalUsers = mapExternalUsersToInstitutionTimezonePaginated(externalUsers.externalUsers, timezoneOffset);

    return {
        ...state,
        externalUsersForStudyWithPagination: timeZoneCorrectedExternalUsers,
        externalUsersForStudyCount: externalUsers.totalCount,
        binder: {
            ...state.binder,
            users: binderUsers,
        }
    };
}

export function waitingForLoadExternalUsersForStudy(state, action) {
    if (!action || action.type !== WAITING_FOR_LOAD_EXTERNAL_USERS_FOR_STUDY) {
        return state;
    }

    const loading = action.payload;

    if (loading) {
        return {
            ...state,
            externalUsersForStudyLoading: loading,
        };
    } else {
        return lodash.omit(state, 'externalUsersForStudyLoading');
    }
}

export function clearExternalUserForStudy(state, action) {
    if (!action || action.type !== CLEAR_EXTERNAL_USER_FOR_STUDY) {
        return state;
    }

    return lodash.omit(state, 'existingExternalUser');
}

export const setStudyNotifiedUsersLoadedStatusReducer = (state, action) => {
    if (action.type === SET_STUDY_NOTIFIED_USERS_LOADED_STATUS) {
        return {
            ...state,
            studySettings: {
                ...state.studySettings,
                studyNotifiedUsersLoaded: action.payload
            }
        };
    }

    return state;
};

export const loadStudyNotifiedUsersSuccessReducer = (state, action) => {
    if (action.type === LOAD_STUDY_NOTIFIED_USERS_SUCCESS) {
        return {
            ...state,
            studySettings: {
                ...state.studySettings,
                studyNotifiedUsers: action.payload
            }
        };
    }

    return state;
};

export const saveStudyNotifiedUsersSuccessReducer = (state, action) => {
    if (action.type === SAVE_STUDY_NOTIFIED_USERS_SUCCESS) {
        return {
            ...state,
            studySettings: {
                ...state.studySettings,
                studyNotifiedUsers: action.payload
            }
        };
    }

    return state;
};

export const clearStudyNotifiedUsersReducer = (state, action) => {
    if (action.type === CLEAR_STUDY_NOTIFIED_USERS) {
        return {
            ...state,
            studySettings: {
                ...state.studySettings,
                studyNotifiedUsers: []
            }
        };
    }

    return state;
};

export const addedStudyDelegationsReducer = (state, action) => {
    if (action.type === ADDED_STUDY_DELEGATIONS) {
        return {
            ...state,
            doaStudyDelegations: [...state.doaStudyDelegations, ...action.payload]
        };
    }

    return state;
};

export default composeReducers(
    getStudyStages,
    getStudyRoles,
    getStudyDescriptors,
    getBinderDescriptors,
    getTeamMembersByInstitution,
    createPotentialResearchersToStudyTeam,
    unloadStudySettingsTeam,
    updateStudyTeam,
    processingStudySettings,
    loadExternalUserForStudy,
    loadExternalUsersForStudy,
    loadExternalUsersForStudyWithPagination,
    getStudyTeam,
    clearExternalUserForStudy,
    waitingForLoadExternalUserForStudy,
    waitingForLoadExternalUsersForStudy,
    setStudyNotifiedUsersLoadedStatusReducer,
    loadStudyNotifiedUsersSuccessReducer,
    saveStudyNotifiedUsersSuccessReducer,
    clearStudyNotifiedUsersReducer,
    addedStudyDelegationsReducer
);

function mapExternalUsersToInstitutionTimezone(externalUsers, timezoneOffset) {
    return externalUsers.map(externalUser => {
        const start = applyTimeZoneToDate(externalUser.binderExpiration.startDate, timezoneOffset).format();
        let end = undefined;

        if (externalUser.binderExpiration && moment(externalUser.binderExpiration.endDate).isBefore('9998-01-01')) {
            end = applyTimeZoneToDate(externalUser.binderExpiration.endDate, timezoneOffset).format();
        }

        return {
            ...externalUser,
            startDate: start.split('T')[0],
            startTime: start.split('T')[1].slice(0, 5),
            endDate: end ? end.split('T')[0] : undefined,
            endTime: end ? end.split('T')[1].slice(0, 5) : undefined,
        };
    });
}

function sortExternalUsersByStartDate(externalUsers) {
    return lodash.orderBy(externalUsers, externalUsers => new Date(externalUsers.startDate), 'desc');
}

function mapExternalUsersToInstitutionTimezonePaginated(externalUsers, timezoneOffset) {
    return externalUsers.map(externalUser => {
        const start = applyTimeZoneToDate(externalUser.startDate, timezoneOffset).format();
        let end = undefined;

        if (externalUser && moment(externalUser.endDate).isBefore('9998-01-01')) {
            end = applyTimeZoneToDate(externalUser.endDate, timezoneOffset).format();
        }

        return {
            ...externalUser,
            startDate: start.split('T')[0],
            startTime: start.split('T')[1].slice(0, 5),
            endDate: end ? end.split('T')[0] : undefined,
            endTime: end ? end.split('T')[1].slice(0, 5) : undefined,
        };
    });
}
